<!-- src/pages/RecipesView.vue -->
<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useQuery } from '@vue/apollo-composable'
import { GET_RECIPES, GET_ALL_RECIPE_CATEGORIES, GET_ALL_INGREDIENTS } from '../graphql/queries'
import { showSuccess } from '../utils/errorHandling'
import CreateRecipeModal from '../components/CreateRecipeModal.vue'
import CreateMealModal from '../components/CreateMealModal.vue'
import IngredientSelector from '../components/IngredientSelector.vue'
import RecipeCard from '../components/RecipeCard.vue'
import LoadingStates from '../components/LoadingStates.vue'
import type { Recipe } from '../components/RecipeCard.vue'

const router = useRouter()
const route = useRoute()

// State
const searchQuery = ref('')
const selectedCategory = ref('')
const selectedIngredients = ref<string[]>([])
const currentPage = ref(1)
const pageSize = ref(12)
const showCreateModal = ref(false)
const showIngredientSelector = ref(false)
const showMealModal = ref(false)
const preselectedRecipe = ref<any>(null)

// GraphQL queries - fetch all recipes without pagination initially
const { result: recipesResult, loading: loadingRecipes, refetch: refetchRecipes } = useQuery(GET_RECIPES, {
  limit: 100, // Get a large number initially
  page: 0,
  search: ''
})
const { result: categoriesResult, loading: loadingCategories } = useQuery(GET_ALL_RECIPE_CATEGORIES)
const { result: ingredientsResult, loading: loadingIngredients } = useQuery(GET_ALL_INGREDIENTS)

// Computed properties
const recipes = computed(() => {
  const recipeData = recipesResult.value?.recipes || []
  return recipeData
})
const categories = computed(() => {
  const categoryData = categoriesResult.value?.getAllRecipeCategories || []
  return categoryData
})
const ingredients = computed(() => {
  const ingredientData = ingredientsResult.value?.getAllIngredients || []
  return ingredientData
})

// Filtered recipes based on search and filters
const filteredRecipes = computed(() => {
  let filtered = recipes.value

  // Search by title (not name as per GraphQL schema)
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    filtered = filtered.filter((recipe: any) =>
      recipe.title.toLowerCase().includes(query) ||
      recipe.description?.toLowerCase().includes(query)
    )
  }

  // Filter by category_id - check both string and number comparison
  if (selectedCategory.value) {
    filtered = filtered.filter((recipe: any) => {
      return recipe.category?.id === selectedCategory.value ||
        recipe.category?.id === parseInt(selectedCategory.value)
    })
  }

  // Filter by ingredients - check if recipe contains ANY of the selected ingredients
  if (selectedIngredients.value.length > 0) {
    filtered = filtered.filter((recipe: any) => {
      // Based on GraphQL schema, ingredients have direct id property
      const recipeIngredientIds = recipe.ingredients?.map((ing: any) => ing.id) || []
      return selectedIngredients.value.some(ingredientId =>
        recipeIngredientIds.includes(ingredientId)
      )
    })
  }

  return filtered
})

// Paginated recipes
const paginatedRecipes = computed(() => {
  const startIndex = (currentPage.value - 1) * pageSize.value
  const endIndex = startIndex + pageSize.value
  return filteredRecipes.value.slice(startIndex, endIndex)
})

const totalPages = computed(() => {
  return Math.ceil(filteredRecipes.value.length / pageSize.value)
})

// Combined loading state - wait for all essential data
const isLoading = computed(() => {
  return loadingRecipes.value || loadingCategories.value || loadingIngredients.value
})

// Methods
const clearFilters = () => {
  searchQuery.value = ''
  selectedCategory.value = ''
  selectedIngredients.value = []
  currentPage.value = 1
}


const toggleIngredient = (ingredientId: string) => {
  const index = selectedIngredients.value.indexOf(ingredientId)
  if (index > -1) {
    selectedIngredients.value.splice(index, 1)
  } else {
    selectedIngredients.value.push(ingredientId)
  }
  currentPage.value = 1 // Reset to first page when filtering
}

const onIngredientSelectionChanged = (ingredientIds: string[]) => {
  selectedIngredients.value = ingredientIds
  currentPage.value = 1
}

const onRecipeCreated = (recipe: any) => {
  showCreateModal.value = false
  refetchRecipes()
  showSuccess(`Recipe "${recipe.title}" created successfully!`)
}

const goToPage = (page: number) => {
  currentPage.value = page
}


const onPlanMeal = (recipe: Recipe) => {
  const original = recipes.value.find((r: any) => r.id === recipe.id)
  preselectedRecipe.value = original || null
  showMealModal.value = true
}

const viewRecipe = (recipe: Recipe) => {
  // Find the original recipe data to get the slug
  const originalRecipe = recipes.value.find((r: any) => r.id === recipe.id)
  if (originalRecipe?.slug) {
    router.push(`/recipes/${originalRecipe.slug}`)
  }
}

const transformRecipeData = (recipe: any): Recipe => {
  // Map category name to the expected format
  const categoryMap: { [key: string]: 'vegetables' | 'mainDishes' | 'fish' | 'breakfast' } = {
    'vegetables': 'vegetables',
    'vegetable': 'vegetables',
    'main dishes': 'mainDishes',
    'main dish': 'mainDishes',
    'main': 'mainDishes',
    'fish': 'fish',
    'seafood': 'fish',
    'breakfast': 'breakfast',
    'dessert': 'breakfast' // Map dessert to breakfast for now
  }

  const categoryName = recipe.category?.name?.toLowerCase() || 'main'
  const mappedCategory = categoryMap[categoryName] || 'mainDishes'

  return {
    id: recipe.id,
    name: recipe.title || recipe.name,
    description: recipe.description,
    image: recipe.image,
    category: mappedCategory,
    categoryName: recipe.category?.name,
    ingredientCount: recipe.ingredients?.length || 0,
    difficulty: recipe.difficulty as 'Easy' | 'Medium' | 'Hard' | undefined,
    isFavorite: recipe.isFavorite || false
  }
}


// Watch for route changes
watch(() => route.query.action, (action) => {
  if (action === 'create') {
    showCreateModal.value = true
  }
})

// Reset current page when filters change
watch([searchQuery, selectedCategory, selectedIngredients], () => {
  currentPage.value = 1
})
</script>

<template>
  <div class="page-enter page-container">
    <!-- Header -->
    <div class="page-header">
      <div>
        <h1 class="font-display display-l page-title">Recipes</h1>
        <p class="page-subtitle">Your kitchen. Your creations. Shared with your group.</p>
      </div>
      <button @click="showCreateModal = true" class="ds-btn-primary">+ Create Recipe</button>
    </div>

    <!-- Search + category pills -->
    <div class="filters-section">
      <!-- Search bar -->
      <div class="search-wrap">
        <svg class="search-icon" width="16" height="16" fill="none" viewBox="0 0 24 24">
          <path d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" stroke="#94A3B8" stroke-width="2" stroke-linecap="round"/>
        </svg>
        <input v-model="searchQuery" type="text" placeholder="Search your recipes by name or ingredient..."
          class="search-input" />
        <button v-if="searchQuery" @click="searchQuery = ''" class="search-clear">×</button>
      </div>

      <!-- Category pills + ingredient filter + count -->
      <div class="filter-row">
        <span class="filter-label">Filter:</span>
        <!-- All pill -->
        <button @click="selectedCategory = ''"
          class="filter-pill"
          :class="{ active: selectedCategory === '' }">
          All
        </button>
        <!-- Category pills -->
        <button v-for="cat in categories" :key="cat.id" @click="selectedCategory = selectedCategory === cat.id ? '' : cat.id"
          class="filter-pill"
          :class="{ active: selectedCategory === cat.id }">
          {{ cat.name }}
          <span class="pill-count"> ({{ recipes.filter((r: any) => r.category?.id === cat.id || r.category?.id === parseInt(cat.id)).length }})</span>
        </button>
        <!-- Ingredient filter button -->
        <button @click="showIngredientSelector = true" class="ingredient-filter-btn">
          <svg width="12" height="12" fill="none" viewBox="0 0 24 24"><path d="M3 6h18M7 12h10M11 18h2" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
          Ingredients{{ selectedIngredients.length > 0 ? ` (${selectedIngredients.length})` : '' }}
        </button>
        <!-- Selected ingredient tags -->
        <template v-if="selectedIngredients.length > 0">
          <span v-for="ingredientId in selectedIngredients.slice(0, 3)" :key="ingredientId" class="ingredient-tag">
            {{ ingredients.find((ing: any) => ing.id === ingredientId)?.name || 'Unknown' }}
            <button @click="toggleIngredient(ingredientId)" class="ingredient-tag-remove">×</button>
          </span>
          <span v-if="selectedIngredients.length > 3" @click="showIngredientSelector = true" class="ingredient-overflow">
            +{{ selectedIngredients.length - 3 }} more
          </span>
        </template>
        <!-- Recipe count -->
        <span class="recipe-count">
          {{ filteredRecipes.length }} recipe{{ filteredRecipes.length !== 1 ? 's' : '' }}
        </span>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="recipes-grid">
      <LoadingStates v-for="i in 8" :key="i" type="skeleton-card" />
    </div>

    <!-- Empty State -->
    <div v-else-if="paginatedRecipes.length === 0" class="empty-state">
      <div class="empty-icon">🔍</div>
      <div class="empty-title">
        {{ filteredRecipes.length === 0 && recipes.length > 0 ? 'No recipes found' : 'No recipes yet' }}
      </div>
      <div class="empty-desc">
        {{ filteredRecipes.length === 0 && recipes.length > 0
          ? 'Try a different search or category, or create a new recipe.'
          : 'Your recipe collection is the starting point — add a recipe, then plan it onto your calendar.' }}
      </div>
      <div class="empty-actions">
        <button v-if="filteredRecipes.length === 0 && recipes.length > 0" @click="clearFilters" class="ds-btn-secondary">
          Clear Filters
        </button>
        <button @click="showCreateModal = true" class="ds-btn-primary">+ Create Recipe</button>
      </div>
    </div>

    <!-- Recipes Grid -->
    <div v-else class="recipes-grid">
      <RecipeCard v-for="recipe in paginatedRecipes" :key="recipe.id" :recipe="transformRecipeData(recipe)"
        @click="viewRecipe" @plan="onPlanMeal" />
    </div>

    <!-- Pagination -->
    <div v-if="totalPages > 1" class="pagination">
      <button @click="goToPage(currentPage - 1)" :disabled="currentPage <= 1" class="ds-btn-secondary pagination-prev"
        :class="{ disabled: currentPage <= 1 }">
        Previous
      </button>
      <button v-for="page in Math.min(totalPages, 10)" :key="page" @click="goToPage(page)"
        class="page-btn"
        :class="{ active: currentPage === page }">
        {{ page }}
      </button>
      <button @click="goToPage(currentPage + 1)" :disabled="currentPage >= totalPages" class="ds-btn-secondary"
        :class="{ disabled: currentPage >= totalPages }">
        Next
      </button>
    </div>

    <!-- Create Recipe Modal -->
    <CreateRecipeModal :show="showCreateModal" @close="showCreateModal = false" @created="onRecipeCreated" />

    <!-- Create Meal Modal -->
    <CreateMealModal :show="showMealModal" :preselected-recipe="preselectedRecipe"
      @close="showMealModal = false; preselectedRecipe = null" @created="showMealModal = false; preselectedRecipe = null" />

    <!-- Ingredient Selector Modal -->
    <IngredientSelector :show="showIngredientSelector" :selected-ingredients="selectedIngredients"
      @close="showIngredientSelector = false" @select="onIngredientSelectionChanged" />
  </div>
</template>

<style scoped>
/* ── Page header ── */
.page-header { display: flex; align-items: flex-end; justify-content: space-between; margin-bottom: 32px; flex-wrap: wrap; gap: 16px; }
.page-title  { letter-spacing: -1px; }
.page-subtitle { font-size: 14px; color: #64748B; margin-top: 6px; }

/* ── Filters ── */
.filters-section { margin-bottom: 32px; }
.search-wrap { position: relative; margin-bottom: 20px; }
.search-icon { position: absolute; left: 16px; top: 50%; transform: translateY(-50%); }
.search-input {
  width: 100%;
  height: 50px;
  border-radius: 14px;
  border: 1px solid #E2E8F0;
  background: white;
  padding: 0 46px;
  font-size: 14px;
  font-family: Inter, sans-serif;
  color: #0F172A;
  outline: none;
  box-shadow: 0 1px 3px rgba(0,0,0,.04);
  box-sizing: border-box;
}
.search-clear {
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  color: #94A3B8;
  font-size: 18px;
  line-height: 1;
}
.filter-row { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
.filter-label { font-size: 12px; font-weight: 700; color: #94A3B8; letter-spacing: .5px; margin-right: 4px; }
.filter-pill {
  height: 34px;
  border-radius: 9999px;
  border: 1.5px solid #E2E8F0;
  font-family: Inter, sans-serif;
  font-weight: 700;
  font-size: 12px;
  padding: 0 16px;
  cursor: pointer;
  transition: all .15s;
  background: white;
  color: #64748B;
}
.filter-pill.active { background: #4F8646; color: white; border-color: #4F8646; }
.pill-count { opacity: .7; }
.ingredient-filter-btn {
  height: 34px;
  border-radius: 9999px;
  border: 1.5px solid #E2E8F0;
  background: white;
  color: #64748B;
  font-family: Inter, sans-serif;
  font-weight: 700;
  font-size: 12px;
  padding: 0 16px;
  cursor: pointer;
  transition: all .15s;
  display: flex;
  align-items: center;
  gap: 6px;
}
.ingredient-tag {
  height: 34px;
  border-radius: 9999px;
  background: #EDF3EC;
  color: #3F4A3C;
  font-size: 11px;
  font-weight: 700;
  padding: 0 12px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.ingredient-tag-remove { background: none; border: none; cursor: pointer; color: #4F8646; font-size: 14px; line-height: 1; padding: 0; }
.ingredient-overflow {
  height: 34px;
  border-radius: 9999px;
  background: #F1F5F9;
  color: #64748B;
  font-size: 11px;
  font-weight: 700;
  padding: 0 12px;
  display: inline-flex;
  align-items: center;
  cursor: pointer;
}
.recipe-count { font-size: 12px; color: #94A3B8; margin-left: auto; }

/* ── Empty state ── */
.empty-state  { text-align: center; padding: 64px 0; color: #94A3B8; }
.empty-icon   { font-size: 40px; margin-bottom: 16px; }
.empty-title  { font-size: 16px; font-weight: 700; color: #334155; margin-bottom: 8px; }
.empty-desc   { font-size: 14px; margin-bottom: 24px; color: #64748B; }
.empty-actions { display: flex; gap: 10px; justify-content: center; flex-wrap: wrap; }

/* ── Recipes grid ── */
.recipes-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 20px;
}
@media (min-width: 1024px) { .recipes-grid { grid-template-columns: repeat(4, 1fr); } }
@media (max-width: 640px)  { .recipes-grid { grid-template-columns: 1fr; } }

/* ── Pagination ── */
.pagination { display: flex; justify-content: center; align-items: center; gap: 8px; margin-top: 40px; }
.pagination-prev { font-size: 13px; }
.pagination-prev.disabled, .ds-btn-secondary.disabled { opacity: .4; cursor: not-allowed; }
.page-btn {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: 1.5px solid #E2E8F0;
  font-family: Inter, sans-serif;
  font-weight: 700;
  font-size: 13px;
  cursor: pointer;
  transition: all .15s;
  background: white;
  color: #64748B;
}
.page-btn.active { background: #4F8646; color: white; border-color: #4F8646; }
</style>
